import { format } from 'date-fns';
import { IntlFormatters, IntlShape } from 'react-intl';
import enJson from '../../src/international/i18/en.json';
import { subscribeToEvent } from '../commands/sportsCommands';
import {
  betmakersStreamingSports,
  betradarStreamingSports,
  ELEAGUE_SPORT_IDS,
  featuredCategories,
  racingSports,
  sportCategories,
  sportsNotEligibleForLMT,
  ticketResultStatuses,
} from '../data-types/sports/sportsConstants';
import { FEATURED_SPORTS_PAGE_SECTIONS, TEMPLATE_ID } from '../data-types/sports/sportsEnums';
import { EventItem, Race, RaceDetails, SportNavTab } from '../data-types/sports/sportsInterfaces';
import { STREAMING_SUPPLIER } from '../data-types/stream/streamEnums';
import { isFeedbackForumEligible, isProjectPhoenix, shouldDisplayHROnFeaturedPage } from '../international/internationalUtils';
import { PATHS } from '../routing-system/appPaths';
import browserHistory from '../routing-system/browserHistory';
import { enqueueSnackbarPrompt } from './prompts';
import { getSEOSportUrl } from './seo';

export const getCompetitionName = (event: EventItem) => {
  switch (event.templateId) {
    case TEMPLATE_ID.ONE:
    case TEMPLATE_ID.TWO:
      return event.titleHeader || '';
    default:
      return event.competition.name;
  }
};

export const getEventNameInsteadOfTeams = (event: EventItem) => {
  switch (event.templateId) {
    case TEMPLATE_ID.ONE:
      return event.titleHeader || '';
    case TEMPLATE_ID.TWO:
      return (event.title || '').replace('{startTime}', format(new Date(event.startDate), 'HH:mm'));
    default:
      return event.competition.name;
  }
};

export const getSportNameByID = (id: number, intl?: IntlShape) => {
  switch (id) {
    case sportCategories.FOOTBALL:
      return intl ? intl.formatMessage({ id: 'sports.football' }) : enJson['sports.football'];
    case sportCategories.TENNIS:
      return intl ? intl.formatMessage({ id: 'sports.tennis' }) : enJson['sports.tennis'];
    case sportCategories.HORSES:
      return intl ? intl.formatMessage({ id: 'sports.horses' }) : enJson['sports.horses'];
    case sportCategories.CRICKET:
      return intl ? intl.formatMessage({ id: 'sports.cricket' }) : enJson['sports.cricket'];
    case sportCategories.RUGBY:
      return intl ? intl.formatMessage({ id: 'sports.rugby' }) : enJson['sports.rugby'];
    case sportCategories.DARTS:
      return intl ? intl.formatMessage({ id: 'sports.darts' }) : enJson['sports.darts'];
    case sportCategories.SNOOKER:
      return intl ? intl.formatMessage({ id: 'sports.snooker' }) : enJson['sports.snooker'];
    case sportCategories.BOXING:
      return intl ? intl.formatMessage({ id: 'sports.boxing' }) : enJson['sports.boxing'];
    case sportCategories.MMA:
      return intl ? intl.formatMessage({ id: 'sports.mma' }) : enJson['sports.mma'];
    case sportCategories.BASKETBALL:
      return intl ? intl.formatMessage({ id: 'sports.basketball' }) : enJson['sports.basketball'];
    case sportCategories.ICE_HOCKEY:
      return intl ? intl.formatMessage({ id: 'sports.ice_hockey' }) : enJson['sports.ice_hockey'];
    case sportCategories.AMERICAN_FOOTBALL:
      return intl ? intl.formatMessage({ id: 'sports.american_football' }) : enJson['sports.american_football'];
    case sportCategories.GREY_HOUNDS:
      return intl ? intl.formatMessage({ id: 'sports.football' }) : enJson['sports.football'];
    case sportCategories.TABLE_TENNIS:
      return intl ? intl.formatMessage({ id: 'sports.table_tennis' }) : enJson['sports.table_tennis'];
    case sportCategories.BASEBALL:
      return intl ? intl.formatMessage({ id: 'sports.baseball' }) : enJson['sports.baseball'];
    case sportCategories.VOLLEYBALL:
      return intl ? intl.formatMessage({ id: 'sports.volleyball' }) : enJson['sports.volleyball'];
    case sportCategories.GOLF:
      return intl ? intl.formatMessage({ id: 'sports.golf' }) : enJson['sports.golf'];
    case sportCategories.F1:
      return intl ? intl.formatMessage({ id: 'sports.f1' }) : enJson['sports.f1'];
    case sportCategories.ELEAGUES:
      return intl ? intl.formatMessage({ id: 'sports.eleagues' }) : enJson['sports.eleagues'];
    case sportCategories.E_FOOTBALL:
      return intl ? intl.formatMessage({ id: 'sports.efootball' }) : enJson['sports.efootball'];
    case sportCategories.E_BASKETBALL:
      return intl ? intl.formatMessage({ id: 'sports.ebasketball' }) : enJson['sports.ebasketball'];
    default:
      return '';
  }
};

export const redirectToSportListCallback = (sportId: number | string) => () => {
  if (ELEAGUE_SPORT_IDS.includes(Number(sportId))) {
    const sportELeagues = String(getSEOSportUrl(sportCategories.ELEAGUES));
    const sport = String(getSEOSportUrl(sportId));
    browserHistory.push(PATHS.APP.SPORTS.CATEGORY(sportELeagues, sport));
  } else {
    const sport = String(getSEOSportUrl(sportId));
    browserHistory.push(PATHS.APP.SPORTS.SPORT_ID(sport));
  }
};

// tslint:disable-next-line:prefer-array-literal
export const eventDetailDrawerEligibleSports: Array<number | string> = [featuredCategories.FEATURED];

export const getTicketResultStatusByValue = (value: number) => {
  return Object.keys(ticketResultStatuses).find(key => ticketResultStatuses[key] === value);
};

// maps live event states for each sport
export const liveEventStates = (state: string, sportId: number, formatMessage: IntlFormatters['formatMessage']) => {
  switch (state) {
    case '1b':
    case '2b':
    case '3b':
    case '4b':
      switch (sportId) {
        case sportCategories.BASKETBALL:
        case sportCategories.E_BASKETBALL:
          return formatMessage({ id: 'sports.break' }).toUpperCase();
        default:
          return '';
      }
    case '1p':
    case '2p':
    case '3p':
    case '4p':
      switch (sportId) {
        case sportCategories.BASKETBALL:
        case sportCategories.E_BASKETBALL:
          return `${formatMessage({ id: 'sports.basketball_period' })}${state.charAt(0)}`;
        case sportCategories.RUGBY:
          return state.charAt(0) === '1'
            ? formatMessage({ id: 'sports.first' })
            : state.charAt(0) === '2'
              ? formatMessage({ id: 'sports.second' })
              : '';
        case sportCategories.ICE_HOCKEY:
          return `${formatMessage({ id: 'sports.ice_hockey_period' })}${state.charAt(0)}`;
        default:
          return '';
      }
    case '1q':
    case '2q':
    case '3q':
    case '4q':
      switch (sportId) {
        case sportCategories.BASKETBALL:
        case sportCategories.E_BASKETBALL:
          return `${formatMessage({ id: 'sports.basketball_period' })}${state.charAt(0)}`;
        case sportCategories.RUGBY:
          return state.charAt(0) === '1'
            ? formatMessage({ id: 'sports.first' })
            : state.charAt(0) === '2'
              ? formatMessage({ id: 'sports.second' })
              : '';
        case sportCategories.AMERICAN_FOOTBALL:
          return `${
            state.charAt(0) === '1'
              ? formatMessage({ id: 'sports.first' })
              : state.charAt(0) === '2'
                ? formatMessage({ id: 'sports.second' })
                : state.charAt(0) === '3'
                  ? formatMessage({ id: 'sports.third' })
                  : state.charAt(0) === '4'
                    ? formatMessage({ id: 'sports.fourth' })
                    : ''
          } ${formatMessage({ id: 'common.quarter' }).toUpperCase()}`;
        default:
          return '';
      }
    case '1s':
    case '2s':
    case '3s':
    case '4s':
    case '5s':
      switch (sportId) {
        case sportCategories.TENNIS:
          return `${formatMessage({ id: 'common.set' }).toUpperCase()} ${state.charAt(0)}`;
        case sportCategories.RUGBY:
          return state.charAt(0) === '1'
            ? formatMessage({ id: 'sports.first' })
            : state.charAt(0) === '2'
              ? formatMessage({ id: 'sports.second' })
              : '';
        default:
          return '';
      }
    default:
      return '';
  }
};

export const convertRaceToEvent = (race: Race | RaceDetails): EventItem => {
  return {
    id: race.id,
    sportId: race.sportId,
    status: race.status,
    disabled: race.disabled,
    suspended: race.suspended,
    competition: {
      name: race.meetingName,
    },
    rightQuickbet: (race as Race).rightQuickbet ? (race as Race).rightQuickbet : undefined,
    leftQuickbet: undefined,
    live: undefined,
    upcoming: undefined,
    homeTeam: undefined,
    awayTeam: undefined,
    startDate: race.startDate,
    completedAt: undefined,
    gameFormatId: -1,
    raceOffers: (race as RaceDetails).offers ? (race as RaceDetails).offers : undefined,
    statistics: undefined,
    stats: undefined,
    eachWayPlaces: race.eachWayPlaces,
    eachWayReduction: race.eachWayReduction,
    runnerCount: race.nofRunners,
    distance: race.distance,
    isRacing: true,
    streams: 'streams' in race ? race.streams : undefined,
    bettingStatus: 'bettingStatus' in race ? race.bettingStatus : undefined,
    highlighted: 'highlighted' in race ? race.highlighted : undefined,
  };
};

export const isRacingEvent = (sportId: number) => racingSports.includes(sportId);

export const eventEligibleForLMT = (event: EventItem) => {
  const lmtEligible = !sportsNotEligibleForLMT.includes(event.sportId);

  return !event.isOutright && !!event.sourceId && lmtEligible;
};

export const requestEventDetails = async (sportId: number, eventId: number, formatMessage: IntlFormatters['formatMessage']) => {
  try {
    await subscribeToEvent(sportId, eventId);
  } catch (e) {
    enqueueSnackbarPrompt(formatMessage({ id: 'event_detail.error_message' }), { closeOnClick: true });
  }
};

export const getFeaturedPageSections = (
  shouldDisplayTrendingBetsUnderneathCasino: boolean,
  displayLiveEvents: boolean,
  displayUpcomingEvents: boolean,
  canDisplayRAF: boolean,
  feedbackForum: boolean,
) => {
  const displayHR = shouldDisplayHROnFeaturedPage();

  return [
    FEATURED_SPORTS_PAGE_SECTIONS.PROMO_SPACE,
    displayLiveEvents && FEATURED_SPORTS_PAGE_SECTIONS.IN_PLAY,
    FEATURED_SPORTS_PAGE_SECTIONS.FEATURED_CASINO,
    shouldDisplayTrendingBetsUnderneathCasino && FEATURED_SPORTS_PAGE_SECTIONS.TRENDING_BETS,
    displayHR && FEATURED_SPORTS_PAGE_SECTIONS.HORSE_RACING,
    FEATURED_SPORTS_PAGE_SECTIONS.SUPERCHARGED_BETS_BAR,
    displayUpcomingEvents && FEATURED_SPORTS_PAGE_SECTIONS.UPCOMING_EVENTS,
    !shouldDisplayTrendingBetsUnderneathCasino && FEATURED_SPORTS_PAGE_SECTIONS.TRENDING_BETS,
    canDisplayRAF && FEATURED_SPORTS_PAGE_SECTIONS.REFER_A_FRIEND,
    isFeedbackForumEligible(feedbackForum) && FEATURED_SPORTS_PAGE_SECTIONS.FEEDBACK_FORUM,
  ];
};

export const getFeaturedPageSectionsForKenya = (
  displayUpcomingFootballEvents: boolean,
  displayUpcomingBasketballEvents: boolean,
  feedbackForum: boolean,
) => {
  return [
    FEATURED_SPORTS_PAGE_SECTIONS.PROMO_SPACE,
    displayUpcomingFootballEvents && FEATURED_SPORTS_PAGE_SECTIONS.TOP_UPCOMING_FOOTBALL_EVENTS,
    displayUpcomingBasketballEvents && FEATURED_SPORTS_PAGE_SECTIONS.TOP_UPCOMING_BASKETBALL_EVENTS,
    FEATURED_SPORTS_PAGE_SECTIONS.SUPERCHARGED_BETS_BAR,
    FEATURED_SPORTS_PAGE_SECTIONS.FOOTBALL_24_HOURS,
    FEATURED_SPORTS_PAGE_SECTIONS.TRENDING_BETS,
    isFeedbackForumEligible(feedbackForum) && FEATURED_SPORTS_PAGE_SECTIONS.FEEDBACK_FORUM,
  ];
};

export const getEventsPerSport = (events: EventItem[]) => {
  const footballEvents: EventItem[] = [];
  const basketballEvents: EventItem[] = [];

  events.forEach(event => {
    if (event.sportId === sportCategories.FOOTBALL) {
      footballEvents.push(event);
    }

    if (event.sportId === sportCategories.BASKETBALL) {
      basketballEvents.push(event);
    }
  });

  return { footballEvents, basketballEvents };
};

export const getSportImageSource = (category: SportNavTab, isSelected: boolean) => {
  if (isSelected && isProjectPhoenix()) {
    return category.orangeSvg;
  }

  if (isSelected) {
    return category.purpleSvg;
  }

  return category.svg;
};

const isBetmakersStreamingSupplier = (sportId: number) => betmakersStreamingSports.includes(sportId);

const isBetradarStreamingSupplier = (sportId: number) => betradarStreamingSports.includes(sportId);

export const getStreamingSupplier = (sportId: number) => {
  if (isBetmakersStreamingSupplier(sportId)) {
    return STREAMING_SUPPLIER.BETMAKERS;
  }

  if (isBetradarStreamingSupplier(sportId)) {
    return STREAMING_SUPPLIER.BETRADAR;
  }

  return undefined;
};
